/**
 * WHAT: Centralized API configuration — base URL, timeouts, feature flags, and endpoint builders.
 * WHY:  All frontend HTTP calls must resolve against the correct backend host. This single config
 *       ensures the backend URL (dev localhost vs. the customer's deployed host) is managed in one place,
 *       preventing scattered hardcoded URLs and enabling feature flags like Enterprise IAG.
 *
 * Usage:
 *   import API_CONFIG from '../config/api.config';
 *   fetch(API_CONFIG.getEndpoint('/api/databases/reports/'));
 *
 * Configuration precedence (highest wins):
 *   1. window.__DAMA_CONFIG__  - RUNTIME config from public/config.js (COTS: editable
 *                                post-install, no rebuild). See public/config.js.
 *   2. REACT_APP_*             - BUILD-TIME env vars (inlined by CRA; dev convenience).
 *   3. Hardcoded default       - localhost.
 *
 * Environment Variables (build-time):
 *   REACT_APP_API_BASE_URL - Base URL for the API (e.g., http://localhost:8000)
 *   REACT_APP_API_TIMEOUT - Request timeout in milliseconds (default: 30000)
 */

// Runtime config injected by public/config.js (loaded before the bundle). Empty/missing
// values fall through to the build-time env var, then the hardcoded default.
const RUNTIME = (typeof window !== 'undefined' && window.__DAMA_CONFIG__) || {};

const API_CONFIG = {
  /**
   * Base URL for the backend API.
   * Runtime config (window.__DAMA_CONFIG__.apiBaseUrl) wins, then build-time env, then same-origin ''.
   * '' (same-origin) is the correct fallback for single-host installs where Django/WhiteNoise serve the
   * UI and /api from one origin. Using explicit !== undefined/null (not ||) so a deliberately-empty
   * runtime apiBaseUrl is honored instead of falling through (empty string is falsy).
   */
  BASE_URL:
    (RUNTIME.apiBaseUrl !== undefined && RUNTIME.apiBaseUrl !== null)
      ? RUNTIME.apiBaseUrl
      : (process.env.REACT_APP_API_BASE_URL !== undefined && process.env.REACT_APP_API_BASE_URL !== null)
        ? process.env.REACT_APP_API_BASE_URL
        : '',

  /**
   * Feature Flags
   * Enable/disable optional features
   */
  FEATURES: {
    /**
     * Enterprise IAG Module
     * When enabled, shows "Enterprise IAG" menu option linking to DAMA Enterprise.
     * Runtime config wins; otherwise build-time env (defaults enabled for back-compat).
     */
    ENTERPRISE_IAG_ENABLED: RUNTIME.enterpriseIagEnabled !== undefined
      ? !!RUNTIME.enterpriseIagEnabled
      : (process.env.REACT_APP_ENTERPRISE_IAG_ENABLED === 'true' || true),
    // Runtime config URL wins, then build-time env, then localhost. No host-name auto-detect.
    ENTERPRISE_IAG_URL: RUNTIME.enterpriseIagUrl ||
      process.env.REACT_APP_ENTERPRISE_IAG_URL ||
      'http://localhost:3001',
  },

  /**
   * Request timeout in milliseconds
   * Reads from environment variable or defaults to 30 seconds
   */
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,

  /**
   * Helper method to construct full API endpoint URLs
   * Ensures proper URL formatting and prevents double slashes
   *
   * @param {string} path - API endpoint path (e.g., '/api/databases/reports/')
   * @returns {string} Full URL to the endpoint
   *
   * @example
   * API_CONFIG.getEndpoint('/api/databases/reports/')
   * // Returns: 'http://localhost:8000/api/databases/reports/'
   */
  getEndpoint: (path) => {
    const base = API_CONFIG.BASE_URL;
    // Remove trailing slash from base if present
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    // Ensure path starts with slash
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
  },

  /**
   * Helper method to construct API endpoints with base path
   * Useful for endpoints that all share a common base like /api/databases/
   *
   * @param {string} path - API endpoint path relative to /api/databases/
   * @returns {string} Full URL to the endpoint
   *
   * @example
   * API_CONFIG.getDatabasesEndpoint('reports/campaign/')
   * // Returns: 'http://localhost:8000/api/databases/reports/campaign/'
   */
  getDatabasesEndpoint: (path) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return API_CONFIG.getEndpoint(`/api/databases/${cleanPath}`);
  },

  /**
   * Helper method to construct enterprise users API endpoints
   *
   * @param {string} path - API endpoint path relative to /api/databases/enterprise-users/
   * @returns {string} Full URL to the endpoint
   *
   * @example
   * API_CONFIG.getEnterpriseUsersEndpoint('changes/')
   * // Returns: 'http://localhost:8000/api/databases/enterprise-users/changes/'
   */
  getEnterpriseUsersEndpoint: (path) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return API_CONFIG.getEndpoint(`/api/databases/enterprise-users/${cleanPath}`);
  },

  /**
   * Standard fetch options with timeout
   * Can be spread into fetch calls for consistent configuration
   *
   * @example
   * fetch(url, { ...API_CONFIG.fetchOptions, method: 'POST', body: data })
   */
  fetchOptions: {
    headers: {
      'Content-Type': 'application/json',
    },
    // Note: Timeout is handled via AbortController in modern browsers
  },

  /**
   * Create an AbortController with timeout
   * Use this for fetch requests that need timeout handling
   *
   * @returns {object} { signal: AbortSignal, timeoutId: number }
   *
   * @example
   * const { signal, timeoutId } = API_CONFIG.createAbortController();
   * try {
   *   const response = await fetch(url, { signal });
   *   clearTimeout(timeoutId);
   * } catch (error) {
   *   if (error.name === 'AbortError') {
   *     console.error('Request timed out');
   *   }
   * }
   */
  createAbortController: () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
    return { signal: controller.signal, timeoutId };
  }
};

// Validate configuration on load (development only)
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    BASE_URL: API_CONFIG.BASE_URL,
    BASE_URL_SOURCE: RUNTIME.apiBaseUrl
      ? 'window.__DAMA_CONFIG__'
      : (process.env.REACT_APP_API_BASE_URL ? 'REACT_APP_API_BASE_URL' : 'default'),
    TIMEOUT: API_CONFIG.TIMEOUT,
    NODE_ENV: process.env.NODE_ENV
  });

  // Warn if using default localhost URL in production build
  if (process.env.NODE_ENV === 'production' && API_CONFIG.BASE_URL === 'http://localhost:8000') {
    console.warn('WARNING: Using localhost URL in production build. Set REACT_APP_API_BASE_URL environment variable.');
  }
}

export default API_CONFIG;
